import {type ReactNode, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {ChipsField, type Opcion} from '@/components/admin-catalogo/chips-field'
import {
  DESTINOS_REQUISITO,
  FAMILIAS_TERMINO,
  TIPOS_REQUISITO,
  opcionesCondicion,
} from '@/components/admin-catalogo/listas-cerradas'
import {
  useCrearCondicion,
  useCrearPerfil,
  useCrearTermino,
  useEditarCondicion,
  useEditarPerfil,
  useEditarTermino,
  type CatalogoCondicion,
  type CatalogoPerfil,
  type CatalogoTermino,
  type TerminoFamilia,
} from '@/hooks/inci/catalogo/use-admin-catalogo'
import {translateApiError} from '@/lib/errors'
import {Hint} from '@/components/ui/hint'
import {tope} from '@/lib/campos-texto'

// Sin elemento de formulario nativo: el envío es un botón del pie, así que el Enter de un chip no envía.

const CLAVE_RE = /^[a-z0-9_]+$/

type Modo<T> = T | 'nuevo' | null

function Campo({id, label, hint, children}: {id: string; label: string; hint?: string; children: ReactNode}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <Hint>{hint}</Hint>}
    </div>
  )
}

function Armazon({
  testId,
  open,
  titulo,
  error,
  pendiente,
  onCerrar,
  onGuardar,
  children,
}: {
  testId: string
  open: boolean
  titulo: string
  error: string | null
  pendiente: boolean
  onCerrar: () => void
  onGuardar: () => void
  children: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <Dialog open={open} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent data-testid={testId} className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">{children}</div>
        {error && (
          <p className="text-sm text-destructive" data-testid={`${testId}-error`}>
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCerrar}>
            {t('common.cancel')}
          </Button>
          <Button type="button" data-testid={`${testId}-submit`} disabled={pendiente} onClick={onGuardar}>
            {pendiente ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** La clave es lo que un perfil cita: solo se escribe al crear. El `id` lo pone cada familia
 * porque uno fijo se duplicaría en el DOM si Radix dejara de desmontar el contenido cerrado. */
function CampoClave({
  id,
  clave,
  setClave,
  editando,
}: {
  id: string
  clave: string
  setClave: (v: string) => void
  editando: boolean
}) {
  const {t} = useTranslation()
  return (
    <Campo id={id} label={t('adminCatalogo.form.clave')} hint={t('adminCatalogo.form.claveHint')}>
      <Input
        maxLength={tope('CODE')}
        id={id}
        data-testid="catalogo-clave-input"
        value={clave}
        disabled={editando}
        onChange={(e) => setClave(e.target.value)}
      />
    </Campo>
  )
}

function numeroONulo(texto: string): number | null {
  return texto.trim() ? Number(texto) : null
}

function textoONulo(texto: string): string | null {
  return texto.trim() ? texto.trim() : null
}

function opcionesDe(valores: readonly string[], prefijo: string, t: (k: string) => string): Opcion[] {
  return valores.map((value) => ({value, label: t(`${prefijo}.${value}`)}))
}

type Opciones = {onSuccess: () => void; onError: (err: unknown) => void}

/** Sin fila crea la versión 1; con fila edita con `version_base`, para que el servidor rechace una foto vieja. */
function enviar<T extends object>(
  fila: {clave: string; version: number} | null,
  clave: string,
  cuerpo: T,
  crear: (vars: T & {clave: string}, opciones: Opciones) => void,
  editar: (vars: T & {clave: string; version_base: number}, opciones: Opciones) => void,
  onCerrar: () => void,
  setError: (mensaje: string) => void
) {
  const opciones: Opciones = {onSuccess: onCerrar, onError: (err) => setError(translateApiError(err))}
  if (fila) editar({...cuerpo, clave: fila.clave, version_base: fila.version}, opciones)
  else crear({...cuerpo, clave}, opciones)
}

export function PerfilDialog({modo, onCerrar}: {modo: Modo<CatalogoPerfil>; onCerrar: () => void}) {
  const {t} = useTranslation()
  const crear = useCrearPerfil()
  const editar = useEditarPerfil()
  const fila = modo !== null && modo !== 'nuevo' ? modo : null
  const [clave, setClave] = useState('')
  const [cargo, setCargo] = useState('')
  const [ciiu, setCiiu] = useState<string[]>([])
  const [clases, setClases] = useState<string[]>([])
  const [funciones, setFunciones] = useState<string[]>([])
  const [peligros, setPeligros] = useState<string[]>([])
  const [condiciones, setCondiciones] = useState<string[]>([])
  const [requerimientos, setRequerimientos] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  // Controlado desde afuera: Radix solo reporta por `onOpenChange` las aperturas del usuario.
  useEffect(() => {
    if (modo === null) return
    setError(null)
    setClave(fila?.clave ?? '')
    setCargo(fila?.cargo_nombre ?? '')
    setCiiu(fila?.ciiu_prefijos ?? [])
    setClases((fila?.clases_riesgo ?? []).map(String))
    setFunciones(fila?.funciones ?? [])
    setPeligros(fila?.peligros ?? [])
    setCondiciones(fila?.condiciones ?? [])
    setRequerimientos(fila?.requerimientos ?? [])
    // Solo el destino importa para precargar; su identidad de objeto, no.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo])

  function guardar() {
    if (!fila && !CLAVE_RE.test(clave)) return setError(t('adminCatalogo.form.claveInvalida'))
    if (!cargo.trim()) return setError(t('adminCatalogo.form.cargoRequerido'))
    if (clases.some((c) => !/^[1-5]$/.test(c))) return setError(t('adminCatalogo.form.claseInvalida'))
    setError(null)
    const cuerpo = {
      cargo_nombre: cargo.trim(),
      ciiu_prefijos: ciiu,
      clases_riesgo: clases.map(Number),
      funciones,
      peligros,
      condiciones,
      requerimientos,
    }
    enviar(fila, clave, cuerpo, crear.mutate, editar.mutate, onCerrar, setError)
  }

  return (
    <Armazon
      testId="catalogo-perfil-dialog"
      open={modo !== null}
      titulo={fila ? t('adminCatalogo.form.editarPerfil') : t('adminCatalogo.form.nuevoPerfil')}
      error={error}
      pendiente={crear.isPending || editar.isPending}
      onCerrar={onCerrar}
      onGuardar={guardar}>
      <CampoClave id="catalogo-perfil-clave" clave={clave} setClave={setClave} editando={!!fila} />
      <Campo id="catalogo-cargo" label={t('adminCatalogo.form.cargo')}>
        <Input
          maxLength={tope('CARGO')}
          id="catalogo-cargo"
          data-testid="catalogo-cargo-input"
          value={cargo}
          onChange={(e) => setCargo(e.target.value)}
        />
      </Campo>
      <ChipsField
        id="catalogo-ciiu"
        label={t('adminCatalogo.form.ciiu')}
        hint={t('adminCatalogo.form.ciiuHint')}
        values={ciiu}
        onChange={setCiiu}
      />
      <ChipsField
        id="catalogo-clases"
        label={t('adminCatalogo.form.clases')}
        hint={t('adminCatalogo.form.clasesHint')}
        values={clases}
        onChange={setClases}
      />
      <ChipsField
        id="catalogo-funciones"
        label={t('adminCatalogo.form.funciones')}
        values={funciones}
        onChange={setFunciones}
      />
      <ChipsField
        id="catalogo-peligros"
        label={t('adminCatalogo.form.peligros')}
        values={peligros}
        onChange={setPeligros}
      />
      <ChipsField
        id="catalogo-condiciones"
        label={t('adminCatalogo.form.condiciones')}
        hint={t('adminCatalogo.form.condicionesHint')}
        values={condiciones}
        onChange={setCondiciones}
        opciones={opcionesCondicion()}
      />
      <ChipsField
        id="catalogo-requerimientos"
        label={t('adminCatalogo.form.requerimientos')}
        values={requerimientos}
        onChange={setRequerimientos}
      />
    </Armazon>
  )
}

/** Las cinco columnas cuyo cambio TUMBA la firma. El nombre visible no está:
 * renombrar «Curso de alturas» no es reescribir lo que alguien atestó. */
function tumbaLaFirma(
  fila: CatalogoCondicion,
  cambios: {
    norma_nombre: string | null
    norma_articulo: string | null
    relacion_norma: string | null
    horas: number | null
    vigencia_meses: number | null
  }
): boolean {
  return (
    fila.verificado_por_nombre != null &&
    (fila.norma_nombre !== cambios.norma_nombre ||
      (fila.norma_articulo ?? null) !== cambios.norma_articulo ||
      fila.relacion_norma !== cambios.relacion_norma ||
      fila.horas !== cambios.horas ||
      fila.vigencia_meses !== cambios.vigencia_meses)
  )
}

export function CondicionDialog({modo, onCerrar}: {modo: Modo<CatalogoCondicion>; onCerrar: () => void}) {
  const {t} = useTranslation()
  const crear = useCrearCondicion()
  const editar = useEditarCondicion()
  const fila = modo !== null && modo !== 'nuevo' ? modo : null
  const [clave, setClave] = useState('')
  const [condicion, setCondicion] = useState<string[]>([])
  const [tipo, setTipo] = useState<string[]>([])
  const [destino, setDestino] = useState<string[]>([])
  const [nombre, setNombre] = useState('')
  const [norma, setNorma] = useState('')
  const [articulo, setArticulo] = useState('')
  const [relacion, setRelacion] = useState('')
  const [horas, setHoras] = useState('')
  const [vigencia, setVigencia] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (modo === null) return
    setError(null)
    setClave(fila?.clave ?? '')
    setCondicion(fila ? [fila.condicion] : [])
    setTipo(fila ? [fila.tipo] : [])
    setDestino(fila?.destino ? [fila.destino] : [])
    setNombre(fila?.nombre ?? '')
    setNorma(fila?.norma_nombre ?? '')
    setArticulo(fila?.norma_articulo ?? '')
    setRelacion(fila?.relacion_norma ?? '')
    setHoras(fila?.horas != null ? String(fila.horas) : '')
    setVigencia(fila?.vigencia_meses != null ? String(fila.vigencia_meses) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo])

  const cambios = {
    norma_nombre: textoONulo(norma),
    norma_articulo: textoONulo(articulo),
    relacion_norma: textoONulo(relacion),
    horas: numeroONulo(horas),
    vigencia_meses: numeroONulo(vigencia),
  }
  // El aviso va antes de guardar, como pide la spec.
  const avisoFirma = fila ? tumbaLaFirma(fila, cambios) : false

  function guardar() {
    if (!fila && !CLAVE_RE.test(clave)) return setError(t('adminCatalogo.form.claveInvalida'))
    if (condicion.length !== 1) return setError(t('adminCatalogo.form.condicionRequerida'))
    if (tipo.length !== 1) return setError(t('adminCatalogo.form.tipoRequerido'))
    if (!nombre.trim()) return setError(t('adminCatalogo.form.nombreRequerido'))
    setError(null)
    const cuerpo = {
      condicion: condicion[0],
      tipo: tipo[0],
      nombre: nombre.trim(),
      destino: destino[0] ?? null,
      ...cambios,
    }
    enviar(fila, clave, cuerpo, crear.mutate, editar.mutate, onCerrar, setError)
  }

  return (
    <Armazon
      testId="catalogo-condicion-dialog"
      open={modo !== null}
      titulo={fila ? t('adminCatalogo.form.editarCondicion') : t('adminCatalogo.form.nuevaCondicion')}
      error={error}
      pendiente={crear.isPending || editar.isPending}
      onCerrar={onCerrar}
      onGuardar={guardar}>
      <CampoClave id="catalogo-condicion-clave" clave={clave} setClave={setClave} editando={!!fila} />
      <ChipsField
        id="catalogo-condicion"
        label={t('adminCatalogo.form.condicion')}
        values={condicion}
        onChange={(next) => setCondicion(next.slice(-1))}
        opciones={opcionesCondicion()}
      />
      <ChipsField
        id="catalogo-tipo"
        label={t('adminCatalogo.form.tipo')}
        values={tipo}
        onChange={(next) => setTipo(next.slice(-1))}
        opciones={opcionesDe(TIPOS_REQUISITO, 'adminCatalogo.tipo', t)}
      />
      <Campo id="catalogo-nombre" label={t('adminCatalogo.form.nombre')}>
        <Input
          maxLength={tope('NOTA')}
          id="catalogo-nombre"
          data-testid="catalogo-nombre-input"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
      </Campo>
      <ChipsField
        id="catalogo-destino"
        label={t('adminCatalogo.form.destino')}
        values={destino}
        onChange={(next) => setDestino(next.slice(-1))}
        opciones={opcionesDe(DESTINOS_REQUISITO, 'adminCatalogo.destino', t)}
      />
      <Campo id="catalogo-norma" label={t('adminCatalogo.form.norma')}>
        <Input
          maxLength={tope('NOTA')}
          id="catalogo-norma"
          data-testid="catalogo-norma-input"
          value={norma}
          onChange={(e) => setNorma(e.target.value)}
        />
      </Campo>
      <Campo
        id="catalogo-articulo"
        label={t('adminCatalogo.form.articulo')}
        hint={t('adminCatalogo.form.articuloHint')}>
        <Input
          maxLength={tope('NOTA')}
          id="catalogo-articulo"
          data-testid="catalogo-articulo-input"
          value={articulo}
          onChange={(e) => setArticulo(e.target.value)}
        />
      </Campo>
      <Campo id="catalogo-relacion" label={t('adminCatalogo.form.relacion')}>
        <Input
          maxLength={tope('NOTA')}
          id="catalogo-relacion"
          data-testid="catalogo-relacion-input"
          value={relacion}
          onChange={(e) => setRelacion(e.target.value)}
        />
      </Campo>
      <Campo id="catalogo-horas" label={t('adminCatalogo.form.horas')}>
        <Input
          id="catalogo-horas"
          data-testid="catalogo-horas-input"
          type="number"
          min={1}
          value={horas}
          onChange={(e) => setHoras(e.target.value)}
        />
      </Campo>
      <Campo id="catalogo-vigencia" label={t('adminCatalogo.form.vigencia')}>
        <Input
          id="catalogo-vigencia"
          data-testid="catalogo-vigencia-input"
          type="number"
          min={1}
          value={vigencia}
          onChange={(e) => setVigencia(e.target.value)}
        />
      </Campo>
      {avisoFirma && (
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="catalogo-aviso-firma">
          {t('adminCatalogo.form.avisoFirma')}
        </p>
      )}
    </Armazon>
  )
}

export function TerminoDialog({modo, onCerrar}: {modo: Modo<CatalogoTermino>; onCerrar: () => void}) {
  const {t} = useTranslation()
  const crear = useCrearTermino()
  const editar = useEditarTermino()
  const fila = modo !== null && modo !== 'nuevo' ? modo : null
  const [clave, setClave] = useState('')
  const [familia, setFamilia] = useState<string[]>([])
  const [ancla, setAncla] = useState('')
  const [terminos, setTerminos] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (modo === null) return
    setError(null)
    setClave(fila?.clave ?? '')
    setFamilia(fila ? [fila.familia] : [])
    setAncla(fila?.ancla ?? '')
    setTerminos(fila?.terminos ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo])

  const esCargo = familia[0] === 'cargo'

  function guardar() {
    if (!fila && !CLAVE_RE.test(clave)) return setError(t('adminCatalogo.form.claveInvalida'))
    if (familia.length !== 1) return setError(t('adminCatalogo.form.familiaRequerida'))
    // `ancla` NULL ⇒ familia `cargo`; NOT NULL ⇒ las otras tres. Lo valida el
    // backend, pero decirlo acá ahorra un 422 que no explica nada.
    if (esCargo && ancla.trim()) return setError(t('adminCatalogo.form.anclaSobra'))
    if (!esCargo && !ancla.trim()) return setError(t('adminCatalogo.form.anclaFalta'))
    if (terminos.length < 2) return setError(t('adminCatalogo.form.grupoCorto'))
    setError(null)
    const cuerpo = {
      familia: familia[0] as TerminoFamilia,
      ancla: esCargo ? null : ancla.trim(),
      terminos,
    }
    enviar(fila, clave, cuerpo, crear.mutate, editar.mutate, onCerrar, setError)
  }

  return (
    <Armazon
      testId="catalogo-termino-dialog"
      open={modo !== null}
      titulo={fila ? t('adminCatalogo.form.editarTermino') : t('adminCatalogo.form.nuevoTermino')}
      error={error}
      pendiente={crear.isPending || editar.isPending}
      onCerrar={onCerrar}
      onGuardar={guardar}>
      <CampoClave id="catalogo-termino-clave" clave={clave} setClave={setClave} editando={!!fila} />
      <ChipsField
        id="catalogo-familia"
        label={t('adminCatalogo.form.familia')}
        values={familia}
        onChange={(next) => setFamilia(next.slice(-1))}
        opciones={opcionesDe(FAMILIAS_TERMINO, 'adminCatalogo.familia', t)}
      />
      {!esCargo && (
        <Campo id="catalogo-ancla" label={t('adminCatalogo.form.ancla')} hint={t('adminCatalogo.form.anclaHint')}>
          <Input
            maxLength={tope('NOTA')}
            id="catalogo-ancla"
            data-testid="catalogo-ancla-input"
            value={ancla}
            onChange={(e) => setAncla(e.target.value)}
          />
        </Campo>
      )}
      <ChipsField
        id="catalogo-terminos"
        label={t('adminCatalogo.form.terminos')}
        hint={t('adminCatalogo.form.terminosHint')}
        values={terminos}
        onChange={setTerminos}
      />
    </Armazon>
  )
}
